import { useMemo, useRef, useState } from "react";

import { useNavigate } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

import {
  useAIModels,
  useAIReviews,
  useRoster,
  useRunAIReview,
  type AIReviewOut,
  type RosterParent,
} from "../api";
import { ReviewVerdict } from "./AIReviewCard";
import { ModelPickerButton, storedModel } from "./ModelPickerButton";

interface BatchState {
  done: number;
  total: number;
  currentId: string | null;
  currentName: string | null;
}

/**
 * The AI Review tab: every parent group with its latest verdict, and a batch
 * runner that sweeps ALL of them in one go. Each group is one Ollama call
 * (seconds to a minute), so the batch runs client-side one group at a time —
 * a single fat request would hit the gateway timeout, and this way progress
 * is live, the run is cancellable, and finished verdicts survive a stop.
 */
export function AIReviewPanel() {
  const navigate = useNavigate();
  const roster = useRoster();
  const reviews = useAIReviews();
  const models = useAIModels();
  const run = useRunAIReview();

  const [model, setModel] = useState<string>(storedModel);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [batch, setBatch] = useState<BatchState | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const cancelRef = useRef(false);

  const parents = useMemo<RosterParent[]>(
    () => roster.data?.parents ?? [],
    [roster.data],
  );
  const reviewByParent = useMemo(() => {
    const m = new Map<string, AIReviewOut>();
    for (const r of reviews.data?.items ?? []) m.set(r.parent_company_id, r);
    return m;
  }, [reviews.data]);

  // Flagged first (they need eyes), then errors, then never-reviewed, then clear.
  const ordered = useMemo(() => {
    const rank = (p: RosterParent) => {
      if (reviewByParent.get(p.id)?.status === "flagged") return 0;
      if (errors[p.id]) return 1;
      if (!reviewByParent.has(p.id)) return 2;
      return 3;
    };
    return [...parents].sort(
      (a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name),
    );
  }, [parents, reviewByParent, errors]);

  const flagged = parents.filter((p) => reviewByParent.get(p.id)?.status === "flagged").length;
  const clear = parents.filter((p) => reviewByParent.get(p.id)?.status === "clear").length;
  const unreviewed = parents.length - flagged - clear;
  const running = batch !== null;
  const reachable = models.data?.reachable ?? false;

  const runAll = async () => {
    const targets = parents.filter((p) => !onlyMissing || !reviewByParent.has(p.id));
    const first = targets[0];
    if (!first) {
      toast.info("Nothing to review — every parent already has a verdict.");
      return;
    }
    cancelRef.current = false;
    setErrors({});
    setBatch({ done: 0, total: targets.length, currentId: first.id, currentName: first.name });
    let flaggedCount = 0;
    let failedCount = 0;
    for (const [i, p] of targets.entries()) {
      if (cancelRef.current) break;
      setBatch({ done: i, total: targets.length, currentId: p.id, currentName: p.name });
      try {
        const verdict = await run.mutateAsync({ parentId: p.id, model: model || null });
        if (verdict.status === "flagged") flaggedCount += 1;
      } catch (e) {
        failedCount += 1;
        setErrors((prev) => ({ ...prev, [p.id]: e instanceof Error ? e.message : String(e) }));
      }
      setBatch({ done: i + 1, total: targets.length, currentId: null, currentName: null });
    }
    const stopped = cancelRef.current;
    setBatch(null);
    if (stopped) {
      toast.info("Batch review stopped — finished verdicts are saved.");
    } else if (failedCount > 0) {
      toast.warning(`Batch done: ${flaggedCount} flagged, ${failedCount} failed.`);
    } else {
      toast.success(
        flaggedCount > 0
          ? `Batch done — ${flaggedCount} parent group(s) flagged.`
          : "Batch done — all relationships look plausible.",
      );
    }
  };

  if (roster.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading relationships from Cinch…</p>;
  }
  if (roster.error) {
    return <p className="text-sm text-destructive">{String(roster.error)}</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      <Card>
        <CardContent className="flex flex-col gap-3 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Sparkles className="h-4 w-4 text-brand" />
            <span className="text-sm font-semibold">Batch relationship review</span>
            <span className="text-xs text-muted-foreground">
              {parents.length} parent groups · {flagged} flagged · {clear} clear ·{" "}
              {unreviewed} not reviewed
            </span>
            <div className="ml-auto flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={onlyMissing}
                  onChange={(e) => setOnlyMissing(e.target.checked)}
                  disabled={running}
                />
                Only not-yet-reviewed
              </label>
              <ModelPickerButton model={model} onModelChange={setModel} disabled={running} />
              {running ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => {
                    cancelRef.current = true;
                  }}
                >
                  Stop after current
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="h-7 text-xs"
                  onClick={runAll}
                  disabled={!reachable || parents.length === 0}
                  title={reachable ? undefined : "The local model host isn't reachable"}
                >
                  Review all
                </Button>
              )}
            </div>
          </div>

          {running && batch && (
            <div className="flex flex-col gap-1.5">
              <Progress value={(batch.done / Math.max(batch.total, 1)) * 100} />
              <p className="text-xs text-muted-foreground">
                {batch.done} of {batch.total} reviewed
                {batch.currentName ? <> — now reading <b>{batch.currentName}</b>…</> : "…"}
                {" "}Each group is one local-model call; you can stop anytime and
                keep what's finished.
              </p>
            </div>
          )}
          {!reachable && !running && (
            <p className="text-xs text-muted-foreground">
              The local model host isn't reachable right now — stored verdicts
              below are still browsable.
            </p>
          )}
        </CardContent>
      </Card>

      {ordered.map((p) => {
        const review = reviewByParent.get(p.id);
        const error = errors[p.id];
        const isCurrent = running && batch?.currentId === p.id;
        return (
          <Card key={p.id}>
            <CardContent className="flex flex-col gap-2 p-3">
              <div className="flex flex-wrap items-center gap-2">
                {review ? (
                  <StatusBadge value={review.status} />
                ) : (
                  <span className="text-xs text-muted-foreground">Not reviewed</span>
                )}
                <button
                  type="button"
                  onClick={() =>
                    navigate({
                      to: "/parent-companies",
                      search: { tab: "ai-review", company: p.id, expanded: true },
                    })
                  }
                  className="text-left text-sm font-medium text-brand hover:underline focus-visible:underline focus-visible:outline-none"
                  title="Open full relationship view"
                >
                  {p.name}
                </button>
                <span className="text-xs text-muted-foreground">
                  {p.child_count} {p.child_count === 1 ? "child" : "children"}
                  {p.stacked ? " · stacked" : ""}
                </span>
                {isCurrent && (
                  <span className="text-xs text-brand">reviewing…</span>
                )}
                <div className="ml-auto">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    disabled={running || !reachable}
                    onClick={() =>
                      run.mutate(
                        { parentId: p.id, model: model || null },
                        { onError: (e) => toast.error(e.message) },
                      )
                    }
                  >
                    {review ? "Re-run" : "Review"}
                  </Button>
                </div>
              </div>
              {error && <p className="text-xs text-destructive">Run failed: {error}</p>}
              {review && <ReviewVerdict data={review} />}
            </CardContent>
          </Card>
        );
      })}
      {parents.length === 0 && (
        <p className="text-sm text-muted-foreground">No parent companies to review.</p>
      )}
    </div>
  );
}
